// QuickBookingPill — ultra-compact booking prompt for hotel detail pages.
//
// Sits right below the hero, above the main content, giving visitors a
// zero-scroll shortcut to enquire without hunting for the full widget.
// On submit, it upserts this hotel into the shared BookingContext with
// the chosen dates + guest count, then smooth-scrolls to the full
// `#plan-your-stay` widget where the visitor can add extras, other
// properties, or complete their contact details.

import { useMemo, useState } from "react";
import {
  Calendar as CalendarIcon, ArrowRight, Minus, Plus,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";

const pick = (obj, lang) => (obj?.[lang] ?? obj?.en ?? "");

const COPY = {
  overline: { en: "Quick book", fr: "Réservation rapide", es: "Reserva rápida" },
  title: {
    en: "Pick dates — we handle the rest",
    fr: "Choisissez vos dates — nous nous occupons du reste",
    es: "Elige tus fechas — nosotros nos ocupamos del resto",
  },
  check_in: { en: "Check-in", fr: "Arrivée", es: "Llegada" },
  check_out: { en: "Check-out", fr: "Départ", es: "Salida" },
  adults: { en: "Adults", fr: "Adultes", es: "Adultos" },
  cta: {
    en: "Continue to enquiry",
    fr: "Continuer la demande",
    es: "Continuar solicitud",
  },
  note: {
    en: "No deposit · reply within 24 h",
    fr: "Sans acompte · réponse en 24 h",
    es: "Sin depósito · respuesta en 24 h",
  },
};

export default function QuickBookingPill({ hotelSlug }) {
  const { lang } = useLanguage();
  const {
    selectedHotels,
    replaceHotelDetail,
    toggleHotel,
    selectedHotelSlugs,
  } = useBooking();

  // Prefill from any existing enquiry for this hotel so the user's
  // in-flight input on the full widget is respected.
  const existing = selectedHotels?.[hotelSlug];
  const [checkIn, setCheckIn] = useState(existing?.check_in || "");
  const [checkOut, setCheckOut] = useState(existing?.check_out || "");
  const [adults, setAdults] = useState(existing?.rooms?.[0]?.adults ?? 2);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const minCheckOut = useMemo(() => {
    if (!checkIn) return today;
    const d = new Date(checkIn);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, [checkIn, today]);

  const onSubmit = () => {
    // Add to enquiry if not already selected, then patch the details.
    const isAdded = selectedHotelSlugs.includes(hotelSlug);
    if (!isAdded) toggleHotel(hotelSlug);
    replaceHotelDetail(hotelSlug, {
      check_in: checkIn || "",
      check_out: checkOut || "",
      notes: existing?.notes || "",
      rooms: [
        {
          room_type: existing?.rooms?.[0]?.room_type || "",
          adults: Number(adults) || 2,
          children: existing?.rooms?.[0]?.children || 0,
          infants: existing?.rooms?.[0]?.infants || 0,
          board_basis: existing?.rooms?.[0]?.board_basis || "breakfast",
        },
        ...(existing?.rooms?.slice(1) || []),
      ],
    });
    const el = document.getElementById("plan-your-stay");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 170;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const inputCls =
    "w-full bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-3 py-3 text-sm text-[#2C2621]";
  const labelCls = "text-[10px] tracking-[0.28em] uppercase text-[#5C5248]";

  return (
    <section
      className="bg-[#FDF8EE] border-y border-[#D4A373]/35"
      data-testid="quick-booking-pill"
      data-no-cms-text="true"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-12 py-5 md:py-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
              {pick(COPY.overline, lang)}
            </p>
            <h3 className="mt-1 font-serif-x text-lg md:text-xl text-[#2C2621] leading-snug">
              {pick(COPY.title, lang)}
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-[repeat(3,minmax(0,1fr))_auto] gap-3 md:gap-4 items-end flex-1 md:max-w-[720px]">
            <label className="flex flex-col gap-1 col-span-1">
              <span className={labelCls}>{pick(COPY.check_in, lang)}</span>
              <div className="relative">
                <CalendarIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5248]"
                  strokeWidth={1.6}
                />
                <input
                  type="date"
                  min={today}
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className={`${inputCls} pl-9`}
                  data-testid="quick-booking-checkin"
                />
              </div>
            </label>

            <label className="flex flex-col gap-1 col-span-1">
              <span className={labelCls}>{pick(COPY.check_out, lang)}</span>
              <div className="relative">
                <CalendarIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5248]"
                  strokeWidth={1.6}
                />
                <input
                  type="date"
                  min={minCheckOut}
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className={`${inputCls} pl-9`}
                  data-testid="quick-booking-checkout"
                />
              </div>
            </label>

            <div className="flex flex-col gap-1 col-span-2 md:col-span-1">
              <span className={labelCls}>{pick(COPY.adults, lang)}</span>
              <div className="flex items-center gap-1.5 bg-white border border-[#2C2621]/15 px-1.5 py-2">
                <button
                  type="button"
                  onClick={() => setAdults((v) => Math.max(1, v - 1))}
                  className="w-7 h-7 inline-flex items-center justify-center text-[#5C5248] hover:text-[#C16542] disabled:opacity-40"
                  disabled={adults <= 1}
                  aria-label="Decrease adults"
                  data-testid="quick-booking-adults-minus"
                >
                  <Minus className="w-3.5 h-3.5" strokeWidth={1.7} />
                </button>
                <span
                  className="flex-1 text-center text-sm tabular-nums text-[#2C2621]"
                  data-testid="quick-booking-adults-value"
                >
                  {adults}
                </span>
                <button
                  type="button"
                  onClick={() => setAdults((v) => v + 1)}
                  className="w-7 h-7 inline-flex items-center justify-center text-[#5C5248] hover:text-[#C16542]"
                  aria-label="Increase adults"
                  data-testid="quick-booking-adults-plus"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={1.7} />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={onSubmit}
              className="col-span-2 md:col-span-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] text-[11px] tracking-[0.28em] uppercase transition-colors"
              data-testid="quick-booking-submit"
            >
              {pick(COPY.cta, lang)}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.7} />
            </button>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-[#5C5248]/80">
          {pick(COPY.note, lang)}
        </p>
      </div>
    </section>
  );
}
